export type VideoSource = 'youtube' | 'vimeo';

export interface ParsedVideo {
  source: VideoSource;
  id: string;
  url: string;
}

export interface VideoMetadata {
  title: string;
  thumbnailUrl?: string;
}

export type VideoLookup =
  | { result: 'valid' | 'unverified'; metadata: VideoMetadata }
  | { result: 'invalid' };

const YOUTUBE_ID = /^[\w-]{11}$/;
const VIMEO_ID = /^\d{5,12}$/;

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
]);

const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com', 'player.vimeo.com']);

const YOUTUBE_PATH_MARKERS = ['embed', 'shorts', 'live', 'v'];
const LOOKUP_TIMEOUT_MS = 8000;

interface OEmbedPayload {
  title?: string;
  thumbnail_url?: string;
  type?: string;
}

export const youtubeThumbnailUrl = (id: string) =>
  `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

const toUrl = (raw: string) => {
  const trimmed = raw.trim().replace(/[.,);]+$/u, '');
  if (!trimmed) return null;

  const href = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const url = new URL(href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return { url, href };
  } catch {
    return null;
  }
};

const youtubeIdFromPath = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);
  const markerIndex = segments.findIndex((segment) =>
    YOUTUBE_PATH_MARKERS.includes(segment),
  );
  const id = markerIndex >= 0 ? segments[markerIndex + 1] : undefined;
  return id && YOUTUBE_ID.test(id) ? id : null;
};

const vimeoIdFromPath = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);
  const markerIndex = segments.findIndex(
    (segment) => segment === 'video' || segment === 'videos',
  );
  const candidate =
    markerIndex >= 0
      ? segments[markerIndex + 1]
      : segments.find((segment) => VIMEO_ID.test(segment));

  return candidate && VIMEO_ID.test(candidate) ? candidate : null;
};

export const parseVideoUrl = (raw: string): ParsedVideo | null => {
  const parsed = toUrl(raw);
  if (!parsed) return null;

  const host = parsed.url.hostname.toLowerCase();

  if (host === 'youtu.be' || host === 'www.youtu.be') {
    const id = parsed.url.pathname.split('/').filter(Boolean)[0];
    if (!id || !YOUTUBE_ID.test(id)) return null;
    return { source: 'youtube', id, url: parsed.href };
  }

  if (YOUTUBE_HOSTS.has(host)) {
    const fromQuery = parsed.url.searchParams.get('v');
    const id =
      fromQuery && YOUTUBE_ID.test(fromQuery)
        ? fromQuery
        : youtubeIdFromPath(parsed.url.pathname);
    if (!id) return null;
    return { source: 'youtube', id, url: parsed.href };
  }

  if (VIMEO_HOSTS.has(host)) {
    const id = vimeoIdFromPath(parsed.url.pathname);
    if (!id) return null;
    return { source: 'vimeo', id, url: parsed.href };
  }

  return null;
};

const oEmbedEndpoint = (parsed: ParsedVideo) => {
  if (parsed.source === 'youtube') {
    const params = new URLSearchParams({
      format: 'json',
      url: `https://www.youtube.com/watch?v=${parsed.id}`,
    });
    return `https://www.youtube.com/oembed?${params.toString()}`;
  }

  const params = new URLSearchParams({ url: parsed.url });
  return `https://vimeo.com/api/oembed.json?${params.toString()}`;
};

const fallbackMetadata = (parsed: ParsedVideo): VideoMetadata => ({
  title: parsed.url,
  thumbnailUrl:
    parsed.source === 'youtube' ? youtubeThumbnailUrl(parsed.id) : undefined,
});

export const lookupVideo = async (
  parsed: ParsedVideo,
): Promise<VideoLookup> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);

  try {
    const response = await fetch(oEmbedEndpoint(parsed), {
      signal: controller.signal,
    });

    if (!response.ok) return { result: 'invalid' };

    const payload = (await response.json()) as OEmbedPayload;
    if (payload.type && payload.type !== 'video') return { result: 'invalid' };

    return {
      result: 'valid',
      metadata: {
        title: payload.title?.trim() || parsed.url,
        thumbnailUrl:
          payload.thumbnail_url || fallbackMetadata(parsed).thumbnailUrl,
      },
    };
  } catch {
    return { result: 'unverified', metadata: fallbackMetadata(parsed) };
  } finally {
    clearTimeout(timeout);
  }
};

export const isSameVideo = (left: ParsedVideo, right: ParsedVideo) =>
  left.source === right.source && left.id === right.id;
